import { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { ACCEPTED_TYPES, MAX_ATTACHMENTS, MAX_FILE_SIZE_BYTES } from '@/lib/attachments'
import { Paperclip } from 'lucide-react'

const LIMITS_HINT = `Up to ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB per file · ${MAX_ATTACHMENTS} max.`

interface SkillAttachmentsLayoutProps {
  canEdit: boolean
  isEmpty: boolean
  isUploading?: boolean
  note?: string
  onFilesSelected: (files: File[]) => void
  children: React.ReactNode
}

export function SkillAttachmentsLayout({ canEdit, isEmpty, isUploading = false, note, onFilesSelected, children }: SkillAttachmentsLayoutProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length > 0) onFilesSelected(files)
  }

  const addButton = (variant: 'default' | 'outline') => (
    <Button
      variant={variant}
      size="sm"
      onClick={() => fileInputRef.current?.click()}
      disabled={isUploading}
      data-testid="skill-add-attachments"
    >
      {isUploading ? 'Uploading…' : 'Add attachments'}
    </Button>
  )

  const hint = <span className="text-xs text-muted-foreground">{note ? `${LIMITS_HINT} ${note}` : LIMITS_HINT}</span>

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {canEdit && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          className="hidden"
          onChange={handleChange}
          aria-label="Upload skill attachments"
        />
      )}
      {isEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20 text-center" data-testid="skill-attachments-empty">
          <p className="text-sm text-foreground">This skill contains no attachments.</p>
          {canEdit && (
            <>
              {addButton('default')}
              {hint}
            </>
          )}
        </div>
      ) : (
        <>
          {canEdit && (
            <div className="flex items-center gap-3">
              {addButton('outline')}
              {hint}
            </div>
          )}
          <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto" aria-label="Skill attachments">
            {children}
          </ul>
        </>
      )}
    </div>
  )
}

interface SkillAttachmentRowProps {
  fileName: string
  details: string
  testId?: string
  children: React.ReactNode
}

export function SkillAttachmentRow({ fileName, details, testId, children }: SkillAttachmentRowProps) {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3" data-testid={testId}>
      <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{fileName}</p>
        <p className="text-xs tabular-nums text-muted-foreground">{details}</p>
      </div>
      {children}
    </li>
  )
}
